import { syntaxTree } from '@codemirror/language';
import { Extension } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view';

// Define types for better readability and type safety
type NodeInfo = { from: number; to: number; name: string };
type DecoratedRange = { from: number; to: number; decoration: Decoration };

// Define a type for parsed path segments
type PathSegment = {
  type: 'property' | 'index' | 'wildcard';
  value: string | number;
};

/**
 * Creates a CodeMirror extension that highlights JSON properties based on variable mappings
 *
 * @param variableMapping - Mapping of variable names to JSON paths
 * @param dataType - The type of data being displayed (input, output, or referenceOutput)
 * @param activeVariable - The currently active variable (will be highlighted differently)
 * @returns A CodeMirror extension that provides syntax highlighting
 */
export function createJsonHighlighterExtension(
  variableMapping: Record<string, string | null>,
  dataType: 'input' | 'output' | 'referenceOutput',
  activeVariable: string | null
): Extension {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.getDecorations(view);
      }

      getDecorations(view: EditorView): DecorationSet {
        // Filter variable mappings to only include those relevant for this data type
        const relevantMappings = Object.entries(variableMapping).filter(
          ([_, path]) => path?.startsWith(`${dataType}.`)
        );

        if (relevantMappings.length === 0) {
          return Decoration.none;
        }

        const ranges: DecoratedRange[] = [];

        // Process each relevant variable mapping
        for (const [variableName, path] of relevantMappings) {
          // Determine if this is the active variable
          const isActiveVariable = activeVariable === variableName;

          // Choose the appropriate highlight class
          const highlightClass = isActiveVariable
            ? 'bg-yellow-200 dark:bg-yellow-800' // yellow for active variable
            : 'bg-green-100 dark:bg-green-950'; // green for other variables

          // Remove the data type prefix to get the actual path
          const actualPath = path?.substring(dataType.length + 1); // +1 for the dot
          if (!actualPath) continue;
          const pathSegments = this.parseJsonPath(actualPath);

          // Find nodes matching the path
          this.findMatchingNodes(view, pathSegments, ranges, highlightClass);
        }

        // Create the decoration set from the ranges
        return Decoration.set(
          ranges.map(({ from, to, decoration }) =>
            Decoration.mark({
              class: decoration.spec.class as string,
            }).range(from, to)
          )
        );
      }

      /**
       * Parse a JSON path string into structured segments that can handle both
       * property access and array indices
       */
      parseJsonPath(path: string): PathSegment[] {
        const segments: PathSegment[] = [];
        let currentSegment = '';
        let inBracket = false;

        // Handle paths like "results.items[0].name" or "results[0][1].items"
        for (let i = 0; i < path.length; i++) {
          const char = path[i];

          if (char === '.' && !inBracket) {
            // End of property segment
            if (currentSegment) {
              segments.push({ type: 'property', value: currentSegment });
              currentSegment = '';
            }
          } else if (char === '[') {
            // Start of array index
            if (currentSegment) {
              segments.push({ type: 'property', value: currentSegment });
              currentSegment = '';
            }
            inBracket = true;
          } else if (char === ']' && inBracket) {
            // End of array index
            if (currentSegment) {
              if (currentSegment === '*') {
                segments.push({ type: 'wildcard', value: '*' });
              } else {
                const index = parseInt(currentSegment, 10);
                segments.push({
                  type: 'index',
                  value: isNaN(index) ? currentSegment : index,
                });
              }
              currentSegment = '';
            }
            inBracket = false;
          } else {
            currentSegment += char;
          }
        }

        // Add the last segment if there is one
        if (currentSegment) {
          // Check if the segment is numeric (for direct array access like array.0.property)
          const numericIndex = parseInt(currentSegment, 10);
          if (currentSegment === '*') {
            segments.push({ type: 'wildcard', value: '*' });
          } else if (
            !isNaN(numericIndex) &&
            numericIndex.toString() === currentSegment
          ) {
            segments.push({ type: 'index', value: numericIndex });
          } else {
            segments.push({ type: 'property', value: currentSegment });
          }
        }

        return segments;
      }

      findMatchingNodes(
        view: EditorView,
        pathSegments: PathSegment[],
        ranges: DecoratedRange[],
        highlightClass: string,
        currentDepth = 0,
        parentFrom = 0,
        parentTo = view.state.doc.length
      ): void {
        // Get the current path segment we're looking for
        const currentSegment = pathSegments[currentDepth];
        if (!currentSegment) return;

        // Only search within the parent node's range
        const tree = syntaxTree(view.state);

        tree.iterate({
          from: parentFrom,
          to: parentTo,
          enter: (node) => {
            // Handle based on the segment type
            if (
              currentSegment.type === 'property' &&
              node.name === 'Property'
            ) {
              // Find the property name (key) and value nodes
              const { keyNode, valueNode } = this.extractPropertyNodes(node);

              if (!keyNode) return;

              // Extract the key name (without quotes)
              const keyName = this.extractKeyName(view, keyNode);

              // If this key matches our current path segment
              if (keyName !== currentSegment.value) return;

              // If this is the last segment in our path, also highlight the value
              if (currentDepth === pathSegments.length - 1 && valueNode) {
                // Add decoration for the key
                this.addDecoration(
                  ranges,
                  keyNode.from,
                  keyNode.to,
                  highlightClass
                );
                this.addDecoration(
                  ranges,
                  valueNode.from,
                  valueNode.to,
                  highlightClass
                );
              }
              // If there are more segments and this value is an object or array,
              // recursively search within it
              else if (currentDepth < pathSegments.length - 1 && valueNode) {
                if (valueNode.name === 'Object' || valueNode.name === 'Array') {
                  this.findMatchingNodes(
                    view,
                    pathSegments,
                    ranges,
                    highlightClass,
                    currentDepth + 1,
                    valueNode.from,
                    valueNode.to
                  );
                }
              }
            }
            // Handle array index access
            else if (
              (currentSegment.type === 'index' ||
                currentSegment.type === 'wildcard') &&
              node.name === 'Array'
            ) {
              // Extract array elements
              const elements = this.extractArrayElements(node);
              if (currentSegment.type === 'index') {
                const targetIndex = currentSegment.value as number;

                // Support negative indices (e.g., -1 refers to the last element)
                const resolvedIndex =
                  targetIndex >= 0
                    ? targetIndex
                    : elements.length + targetIndex;

                // Abort processing this path if the resolved index is out of bounds
                if (resolvedIndex < 0 || resolvedIndex >= elements.length) {
                  return;
                }

                const targetElement = elements[resolvedIndex];

                // If this is the last segment in our path, highlight the entire element
                if (currentDepth === pathSegments.length - 1) {
                  this.addDecoration(
                    ranges,
                    targetElement.from,
                    targetElement.to,
                    highlightClass
                  );
                }
                // If there are more segments, recursively search within the element
                else if (currentDepth < pathSegments.length - 1) {
                  this.findMatchingNodes(
                    view,
                    pathSegments,
                    ranges,
                    highlightClass,
                    currentDepth + 1,
                    targetElement.from,
                    targetElement.to
                  );
                }
              } else if (currentSegment.type === 'wildcard') {
                // Iterate over all elements to apply wildcard logic
                elements.forEach((element) => {
                  if (currentDepth === pathSegments.length - 1) {
                    this.addDecoration(
                      ranges,
                      element.from,
                      element.to,
                      highlightClass
                    );
                  } else if (currentDepth < pathSegments.length - 1) {
                    this.findMatchingNodes(
                      view,
                      pathSegments,
                      ranges,
                      highlightClass,
                      currentDepth + 1,
                      element.from,
                      element.to
                    );
                  }
                });
              }
            }
          },
        });
      }

      // Helper method to extract array elements from an array node
      private extractArrayElements(arrayNode: any): NodeInfo[] {
        const elements: NodeInfo[] = [];

        // Parse the array structure to find individual elements
        const cursor = arrayNode.node.cursor();

        // First child should be the opening bracket
        if (cursor.firstChild()) {
          // Skip the opening bracket and look for elements
          if (cursor.nextSibling()) {
            do {
              // Skip commas, look for actual value nodes
              if (
                cursor.name === 'Expression' ||
                cursor.name === 'Number' ||
                cursor.name === 'String' ||
                cursor.name === 'True' ||
                cursor.name === 'False' ||
                cursor.name === 'Null' ||
                cursor.name === 'Array' ||
                cursor.name === 'Object'
              ) {
                elements.push({
                  from: cursor.from,
                  to: cursor.to,
                  name: cursor.name,
                });
              }
            } while (cursor.nextSibling());
          }
        }

        return elements;
      }

      // Helper method to extract key and value nodes from a property node
      private extractPropertyNodes(node: any): {
        keyNode?: NodeInfo;
        valueNode?: NodeInfo;
      } {
        let keyNode: NodeInfo | undefined;
        let valueNode: NodeInfo | undefined;

        // Iterate through children to find the key and value
        const cursor = node.node.cursor();
        if (cursor.firstChild()) {
          do {
            if (cursor.name === 'PropertyName') {
              keyNode = {
                from: cursor.from,
                to: cursor.to,
                name: cursor.name,
              };
            } else if (
              cursor.name === 'Expression' ||
              cursor.name === 'Number' ||
              cursor.name === 'String' ||
              cursor.name === 'True' ||
              cursor.name === 'False' ||
              cursor.name === 'Null' ||
              cursor.name === 'Array' ||
              cursor.name === 'Object'
            ) {
              valueNode = {
                from: cursor.from,
                to: cursor.to,
                name: cursor.name,
              };
            }
          } while (cursor.nextSibling());
        }

        return { keyNode, valueNode };
      }

      // Helper method to extract the key name from a key node
      private extractKeyName(view: EditorView, keyNode: NodeInfo): string {
        const keyText = view.state.doc.sliceString(keyNode.from, keyNode.to);
        return keyText.replace(/^["'](.*)["']$/, '$1');
      }

      // Helper method to add a decoration to the ranges array
      private addDecoration(
        ranges: DecoratedRange[],
        from: number,
        to: number,
        highlightClass: string
      ): void {
        const decoration = Decoration.mark({
          class: highlightClass,
        });

        ranges.push({
          from,
          to,
          decoration,
        });
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.getDecorations(update.view);
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  );
}
